import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  BugReport as BugIcon,
  Storage as DatabaseIcon,
  Payment as PaymentIcon,
  Message as MessageIcon,
  People as InvitationIcon,
  People,
  NetworkCheck as NetworkIcon,
  Timeline as LogIcon,
  Send as SendIcon,
  PersonAdd as PersonAddIcon,
  Chat as ChatIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import TestUtilities from '../components/Test/TestUtilities';
import axios from 'axios';
import { getApiBaseUrl } from '../utils/apiConfig';

const TestDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [testResults, setTestResults] = useState({});
  const [testRunning, setTestRunning] = useState({});
  const [logs, setLogs] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedTestData, setSelectedTestData] = useState({});
  const [logDialog, setLogDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedTest, setSelectedTest] = useState(null);

  // Enhanced state variables for new features
  const [seekerChatData, setSeekerChatData] = useState({ messages: [], users: {}, thread: null, currentUser: null, otherUser: null });
  const [professionalChatData, setProfessionalChatData] = useState({ messages: [], users: {}, thread: null, currentUser: null, otherUser: null });
  const [newMessage, setNewMessage] = useState({ seeker: '', professional: '' });
  const [chatViewExpanded, setChatViewExpanded] = useState(false);
  const [currentUsers, setCurrentUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  const [selectedUserProfile, setSelectedUserProfile] = useState(null);
  const [profileEditData, setProfileEditData] = useState({});

  const API_BASE = getApiBaseUrl();

  // Test Categories - Professional styled
  const testCategories = [
    { 
      id: 'users', 
      label: 'User Management', 
      icon: PersonAddIcon,
      description: 'Create, manage, and delete test users safely',
      priority: 'high',
      color: '#1e40af',
      bgColor: 'rgba(30, 64, 175, 0.05)'
    },
    { 
      id: 'invitations', 
      label: 'Invitation Workflow', 
      icon: InvitationIcon,
      description: 'Send invitations, accept/reject/reschedule with banner UI',
      priority: 'high',
      color: '#1e40af',
      bgColor: 'rgba(30, 64, 175, 0.05)'
    },
    { 
      id: 'payments', 
      label: 'Payment System', 
      icon: PaymentIcon,
      description: 'Payment authorization, capture, refunds, Stripe integration',
      priority: 'high',
      color: '#059669',
      bgColor: 'rgba(5, 150, 105, 0.05)'
    },
    { 
      id: 'chat', 
      label: 'Dual-View Chat', 
      icon: ChatIcon,
      description: 'Real-time messaging simulation without multiple terminals',
      priority: 'high',
      color: '#1e40af',
      bgColor: 'rgba(30, 64, 175, 0.05)'
    },
    { 
      id: 'system', 
      label: 'System Health', 
      icon: NetworkIcon,
      description: 'Database, API, Socket.IO connectivity and health',
      priority: 'medium',
      color: '#6b7280',
      bgColor: 'rgba(107, 114, 128, 0.05)'
    },
    { 
      id: 'development', 
      label: 'Dev Utilities', 
      icon: BugIcon,
      description: 'Quick helpers, debug tools, development shortcuts',
      priority: 'low',
      color: '#f59e0b',
      bgColor: 'rgba(245, 158, 11, 0.05)'
    }
  ];

  // Utility Functions
  const addLog = useCallback((level, category, message, data = null) => {
    const logEntry = {
      id: Date.now() + Math.random(),
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      user: user?.email || 'Unknown'
    };
    setLogs(prev => [logEntry, ...prev.slice(0, 99)]);
  }, [user]);

  const createApiClient = () => {
    const token = localStorage.getItem('token');
    return axios.create({
      baseURL: API_BASE,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      },
      timeout: 10000
    });
  };

  const updateTestResult = (category, testName, result) => {
    setTestResults(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [testName]: {
          ...result,
          timestamp: new Date().toISOString()
        }
      }
    }));
  };

  const setTestRunningState = (category, testName, running) => {
    setTestRunning(prev => ({
      ...prev,
      [`${category}_${testName}`]: running
    }));
  };

  // Enhanced chat and user management functions
  const refreshChatView = async (userType = null) => {
    if (userType) {
      addLog('info', 'chat', `Refreshing ${userType} chat view...`);
    } else {
      addLog('info', 'chat', 'Refreshing both chat views...');
    }
    
    try {
      const client = createApiClient();
      
      if (!userType || userType === 'seeker') {
        const seekerResponse = await client.get('/test/get-test-chat/seeker');
        if (seekerResponse.data.success) {
          setSeekerChatData({
            messages: seekerResponse.data.data.messages || [],
            users: seekerResponse.data.data.users || {},
            thread: seekerResponse.data.data.thread,
            currentUser: seekerResponse.data.data.currentUser,
            otherUser: seekerResponse.data.data.otherUser
          });
          addLog('success', 'chat', 'Seeker chat view refreshed', seekerResponse.data.data);
        }
      }
      
      if (!userType || userType === 'professional') {
        const professionalResponse = await client.get('/test/get-test-chat/professional');
        if (professionalResponse.data.success) {
          setProfessionalChatData({
            messages: professionalResponse.data.data.messages || [],
            users: professionalResponse.data.data.users || {},
            thread: professionalResponse.data.data.thread,
            currentUser: professionalResponse.data.data.currentUser,
            otherUser: professionalResponse.data.data.otherUser
          });
          addLog('success', 'chat', 'Professional chat view refreshed', professionalResponse.data.data);
        }
      }
    } catch (error) {
      addLog('error', 'chat', 'Failed to refresh chat view', error.message);
    }
  };

  // Send message as specific user type
  const sendTestMessage = async (userType, content) => {
    if (!content.trim()) return;
    
    addLog('info', 'chat', `Sending message as ${userType}: "${content}"`);
    setTestRunningState('chat', 'sendMessage', true);

    try {
      const client = createApiClient();
      const response = await client.post('/test/send-test-message', {
        content: content.trim(),
        asUserType: userType
      });
      
      addLog('success', 'chat', `Message sent as ${userType}`, response.data);
      
      // Clear the input
      setNewMessage(prev => ({ ...prev, [userType]: '' }));
      
      // Refresh chat views after short delay
      setTimeout(() => refreshChatView(), 500);
      
    } catch (error) {
      addLog('error', 'chat', `Failed to send message as ${userType}`, error.message);
    } finally {
      setTestRunningState('chat', 'sendMessage', false);
    }
  };

  // Handle key press for message sending
  const handleKeyPress = (event, userType) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendTestMessage(userType, newMessage[userType]);
    }
  };

  // Fetch current users
  const fetchCurrentUsers = async () => {
    setLoadingUsers(true);
    try {
      addLog('info', 'users', 'Fetching current users...');
      const client = createApiClient();
      
      // Add more detailed logging
      addLog('info', 'users', `Making request to: ${API_BASE}/test/get-current-users`);
      
      const response = await client.get('/test/get-current-users');
      
      addLog('info', 'users', 'Response received', {
        status: response.status,
        success: response.data?.success,
        userCount: response.data?.data?.users?.length
      });
      
      if (response.data && response.data.success) {
        const users = response.data.data?.users || [];
        setCurrentUsers(users);
        addLog('success', 'users', `Found ${users.length} users`, users);
      } else {
        addLog('warning', 'users', 'Unexpected response format', response.data);
        setCurrentUsers([]);
      }
    } catch (error) {
      addLog('error', 'users', 'Failed to fetch current users', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        baseURL: API_BASE
      });
      setCurrentUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    const user = currentUsers.find(u => u._id === userId);
    const userName = user ? `${user.firstName} ${user.lastName}` : userId;
    
    addLog('info', 'users', `Attempting to delete user: ${userName} (${userId})`);
    setTestRunningState('users', 'deleteUser', true);
    
    try {
      const client = createApiClient();
      const response = await client.delete(`/test/delete-user/${userId}`);
      
      if (response.data.success) {
        addLog('success', 'users', `User deleted successfully: ${userName}`, {
          deletedUser: user,
          response: response.data
        });
        setCurrentUsers(prev => prev.filter(user => user._id !== userId));
        
        // Update test results
        updateTestResult('users', 'deleteUser', {
          status: 'completed',
          data: response.data,
          summary: `Successfully deleted user: ${userName}`
        });
      } else {
        addLog('warning', 'users', `Delete operation returned unsuccessful: ${response.data.message || 'Unknown reason'}`, response.data);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      addLog('error', 'users', `Failed to delete user: ${userName}`, {
        error: errorMessage,
        status: error.response?.status,
        userId: userId
      });
      
      updateTestResult('users', 'deleteUser', {
        status: 'error',
        error: errorMessage,
        summary: `Failed to delete user: ${userName}`
      });
    } finally {
      setTestRunningState('users', 'deleteUser', false);
    }
  };

  // Open profile dialog
  const openProfileDialog = (user) => {
    setSelectedUserProfile(user);
    setProfileEditData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      occupation: user.occupation || '',
      industry: user.industry || '',
      experience: user.experience || '',
      location: user.location || ''
    });
    setProfileDialog(true);
    addLog('info', 'users', `Opening profile dialog for: ${user.firstName} ${user.lastName}`, user);
  };

  // Save profile changes (actual update)
  const saveProfileChanges = async () => {
    if (!selectedUserProfile) return;
    
    const userName = `${selectedUserProfile.firstName} ${selectedUserProfile.lastName}`;
    addLog('info', 'users', `Updating profile for: ${userName}`, profileEditData);
    setTestRunningState('users', 'profileUpdate', true);
    
    try {
      const client = createApiClient();
      
      // Get only the changed fields
      const changedFields = {};
      Object.keys(profileEditData).forEach(key => {
        if (profileEditData[key] !== (selectedUserProfile[key] || '')) {
          changedFields[key] = profileEditData[key];
        }
      });
      
      if (Object.keys(changedFields).length === 0) {
        addLog('warning', 'users', 'No changes to save');
        setProfileDialog(false);
        return;
      }
      
      addLog('info', 'users', `Saving changes for ${userName}`, { 
        changes: changedFields,
        userId: selectedUserProfile._id,
        fullUrl: `${API_BASE}/test/update-user-profile/${selectedUserProfile._id}`
      });
      
      console.log('Making PUT request to:', `${API_BASE}/test/update-user-profile/${selectedUserProfile._id}`);
      console.log('Sending data:', changedFields);
      
      const response = await client.put(`/test/update-user-profile/${selectedUserProfile._id}`, changedFields);
      
      if (response.data.success) {
        const updatedUser = response.data.data.user;
        
        addLog('success', 'users', `Profile updated successfully for: ${userName}`, {
          updatedUser,
          updatedFields: response.data.data.updatedFields
        });
        
        // Update the local currentUsers state to reflect changes immediately
        setCurrentUsers(prevUsers => 
          prevUsers.map(user => 
            user._id === selectedUserProfile._id 
              ? { ...user, ...updatedUser }
              : user
          )
        );
        
        updateTestResult('users', 'profileUpdate', {
          status: 'completed',
          data: {
            updatedUser,
            changes: changedFields,
            updatedFields: response.data.data.updatedFields
          },
          summary: `Successfully updated profile for: ${updatedUser.firstName} ${updatedUser.lastName}`
        });
        
        setProfileDialog(false);
        
        // Show success message in logs
        addLog('success', 'users', `✅ Local state updated - changes are now visible in the table`);
        
      } else {
        addLog('warning', 'users', `Update returned unsuccessful: ${response.data.message || 'Unknown reason'}`, response.data);
        
        updateTestResult('users', 'profileUpdate', {
          status: 'error',
          error: response.data.message || 'Update returned unsuccessful'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      addLog('error', 'users', `Failed to update profile for: ${userName}`, {
        error: errorMessage,
        status: error.response?.status,
        userId: selectedUserProfile._id
      });
      
      updateTestResult('users', 'profileUpdate', {
        status: 'error',
        error: errorMessage,
        summary: `Failed to update profile for: ${userName}`
      });
    } finally {
      setTestRunningState('users', 'profileUpdate', false);
    }
  };

  // Fetch invitations
  const fetchInvitations = async () => {
    setLoadingInvitations(true);
    try {
      const client = createApiClient();
      const response = await client.get('/invitations');
      if (response.data.success) {
        setInvitations(response.data.data?.invitations || []);
        addLog('success', 'invitations', `Found ${response.data.data?.invitations?.length || 0} invitations`);
      }
    } catch (error) {
      addLog('error', 'invitations', 'Failed to fetch invitations', error.message);
      setInvitations([]);
    } finally {
      setLoadingInvitations(false);
    }
  };

  // Accept invitation
  const acceptInvitation = async (invitationId) => {
    addLog('info', 'invitations', `Accepting invitation: ${invitationId}`);
    try {
      const client = createApiClient();
      const response = await client.post(`/invitations/${invitationId}/accept`);
      if (response.data.success) {
        addLog('success', 'invitations', 'Invitation accepted', response.data);
        await Promise.all([fetchInvitations(), refreshChatView()]);
      }
    } catch (error) {
      addLog('error', 'invitations', 'Failed to accept invitation', error.message);
    }
  };

  // Reject invitation
  const rejectInvitation = async (invitationId) => {
    addLog('info', 'invitations', `Rejecting invitation: ${invitationId}`);
    try {
      const client = createApiClient();
      const response = await client.post(`/invitations/${invitationId}/reject`);
      if (response.data.success) {
        addLog('success', 'invitations', 'Invitation rejected', response.data);
        await Promise.all([fetchInvitations(), refreshChatView()]);
      }
    } catch (error) {
      addLog('error', 'invitations', 'Failed to reject invitation', error.message);
    }
  };

  // Test functions
  const testCreateTestUsers = async () => {
    addLog('info', 'users', 'Creating test users...');
    setTestRunningState('users', 'createUsers', true);

    try {
      const client = createApiClient();
      const response = await client.post('/test/create-test-users');
      
      updateTestResult('users', 'createUsers', {
        status: 'completed',
        data: response.data,
        summary: response.data.message
      });
      
      addLog('success', 'users', 'Test users created successfully', response.data);
      setTimeout(fetchCurrentUsers, 1000);
    } catch (error) {
      updateTestResult('users', 'createUsers', {
        status: 'error',
        error: error.response?.data?.message || error.message
      });
      addLog('error', 'users', 'Failed to create test users', error.message);
    } finally {
      setTestRunningState('users', 'createUsers', false);
    }
  };

  const testCreateTestChat = async () => {
    addLog('info', 'chat', 'Creating test chat...');
    setTestRunningState('chat', 'createChat', true);

    try {
      const client = createApiClient();
      const response = await client.post('/test/create-test-chat');
      
      updateTestResult('chat', 'createChat', {
        status: 'completed',
        data: response.data,
        summary: response.data.message
      });
      
      addLog('success', 'chat', 'Test chat created successfully', response.data);
    } catch (error) {
      updateTestResult('chat', 'createChat', {
        status: 'error',
        error: error.response?.data?.message || error.message
      });
      addLog('error', 'chat', 'Failed to create test chat', error.message);
    } finally {
      setTestRunningState('chat', 'createChat', false);
    }
  };

  const testCreateInvitation = async (fromUserType = 'seeker') => {
    addLog('info', 'invitations', `Creating test invitation from ${fromUserType}...`);
    setTestRunningState('invitations', 'createInvitation', true);

    try {
      const client = createApiClient();
      const response = await client.post('/test/create-test-invitation', {
        fromUserType,
        message: `Test invitation message from ${fromUserType}`,
        topic: 'Test Coffee Chat Discussion',
        proposedDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      });
      
      updateTestResult('invitations', 'createInvitation', {
        status: 'completed',
        data: response.data,
        summary: response.data.message
      });
      
      addLog('success', 'invitations', `Test invitation created from ${fromUserType}`, response.data);
    } catch (error) {
      updateTestResult('invitations', 'createInvitation', {
        status: 'error',
        error: error.response?.data?.message || error.message
      });
      addLog('error', 'invitations', `Failed to create test invitation from ${fromUserType}`, error.message);
    } finally {
      setTestRunningState('invitations', 'createInvitation', false);
    }
  };

  // System Health Test
  const testSystemHealth = async () => {
    addLog('info', 'system', 'Starting system health check...');
    setTestRunningState('system', 'health', true);

    const tests = {
      serverConnection: { name: 'Server Connection', status: 'pending' },
      databaseConnection: { name: 'Database Connection', status: 'pending' },
      authService: { name: 'Auth Service', status: 'pending' }
    };

    try {
      // Test server connection
      try {
        const response = await axios.get(`${API_BASE}/test`, { timeout: 5000 });
        tests.serverConnection = { 
          name: 'Server Connection', 
          status: 'success', 
          data: response.data
        };
        addLog('success', 'system', 'Server connection test passed', response.data);
      } catch (error) {
        tests.serverConnection = { 
          name: 'Server Connection', 
          status: 'error', 
          error: error.message 
        };
        addLog('error', 'system', 'Server connection test failed', error.message);
      }

      // Test database connection
      try {
        const client = createApiClient();
        const response = await client.get('/test/db');
        tests.databaseConnection = { 
          name: 'Database Connection', 
          status: 'success', 
          data: response.data 
        };
        addLog('success', 'system', 'Database connection test passed', response.data);
      } catch (error) {
        tests.databaseConnection = { 
          name: 'Database Connection', 
          status: 'error', 
          error: error.response?.data?.message || error.message 
        };
        addLog('error', 'system', 'Database connection test failed', error.message);
      }

      updateTestResult('system', 'health', {
        status: 'completed',
        tests,
        summary: {
          total: Object.keys(tests).length,
          passed: Object.values(tests).filter(t => t.status === 'success').length,
          failed: Object.values(tests).filter(t => t.status === 'error').length
        }
      });

    } catch (error) {
      addLog('error', 'system', 'System health check failed', error.message);
      updateTestResult('system', 'health', {
        status: 'error',
        error: error.message
      });
    } finally {
      setTestRunningState('system', 'health', false);
    }
  };

  // Payment Flow Test
  const testPaymentFlow = async () => {
    addLog('info', 'payment', 'Starting payment flow tests...');
    setTestRunningState('payment', 'flow', true);

    const tests = {
      testUserPaymentFlow: { name: 'Test User Payment Flow', status: 'pending' },
      paymentHistory: { name: 'Get Payment History', status: 'pending' }
    };

    try {
      const client = createApiClient();

      // Test payment flow with test users
      try {
        const response = await client.post('/test/test-payment-flow');
        tests.testUserPaymentFlow = {
          name: 'Test User Payment Flow',
          status: 'success',
          data: response.data
        };
        addLog('success', 'payment', 'Test payment flow passed', response.data);
      } catch (error) {
        tests.testUserPaymentFlow = {
          name: 'Test User Payment Flow',
          status: 'error',
          error: error.response?.data?.message || error.message
        };
        addLog('error', 'payment', 'Test payment flow failed', error.message);
      }

      // Test payment history
      try {
        const response = await client.get('/payments/history');
        tests.paymentHistory = {
          name: 'Get Payment History',
          status: 'success',
          data: response.data
        };
        addLog('success', 'payment', 'Payment history test passed', response.data);
      } catch (error) {
        tests.paymentHistory = {
          name: 'Get Payment History',
          status: 'error',
          error: error.response?.data?.message || error.message
        };
        addLog('error', 'payment', 'Payment history test failed', error.message);
      }

      updateTestResult('payment', 'flow', {
        status: 'completed',
        tests,
        summary: {
          total: Object.keys(tests).length,
          passed: Object.values(tests).filter(t => t.status === 'success').length,
          failed: Object.values(tests).filter(t => t.status === 'error').length
        }
      });

    } catch (error) {
      addLog('error', 'payment', 'Payment flow tests failed', error.message);
      updateTestResult('payment', 'flow', {
        status: 'error',
        error: error.message
      });
    } finally {
      setTestRunningState('payment', 'flow', false);
    }
  };

  // Effects
  useEffect(() => {
    fetchCurrentUsers();
    fetchInvitations();
  }, []);

  // REMOVED: Auto-refresh polling - Socket.IO handles real-time updates
  // Users can manually refresh if needed using the refresh button

  // Utility functions for rendering
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckIcon color="success" />;
      case 'error': return <ErrorIcon color="error" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'pending': return <CircularProgress size={20} />;
      default: return <InfoIcon color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      case 'pending': return 'default';
      default: return 'info';
    }
  };

  const renderTestResults = (category) => {
    const categoryResults = testResults[category] || {};
    
    return (
      <Box>
        {Object.entries(categoryResults).map(([testName, result]) => (
          <Accordion key={testName} sx={{ mb: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                {getStatusIcon(result.status)}
                <Typography sx={{ ml: 1, flexGrow: 1 }}>
                  {testName.charAt(0).toUpperCase() + testName.slice(1)} Test
                </Typography>
                <Chip 
                  label={result.status} 
                  color={getStatusColor(result.status)}
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {result.summary && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Tests: {result.summary.total} | 
                  Passed: {result.summary.passed} | 
                  Failed: {result.summary.failed}
                </Alert>
              )}
              
              {result.error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {result.error}
                </Alert>
              )}
              
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                Last run: {new Date(result.timestamp).toLocaleString()}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          <BugIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Test Dashboard
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Comprehensive testing and debugging for GrabACoffee platform
        </Typography>
        {user && (
          <Chip 
            label={`User: ${user.firstName} ${user.lastName} (${user.userType})`}
            color="primary"
            sx={{ mt: 1 }}
          />
        )}
      </Box>

      {/* Control Panel */}
      <Paper 
        sx={{ 
          p: 3, 
          mb: 3,
          background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.03) 0%, rgba(255,255,255,0.05) 100%)',
          border: '1px solid rgba(30, 64, 175, 0.1)'
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ color: '#1e40af', fontWeight: 'bold', mb: 1 }}>
            🎛️ Test Control Panel
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive testing suite for all platform components
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Button 
              variant="contained" 
              startIcon={<PlayIcon />}
              onClick={() => {
                testSystemHealth();
                testPaymentFlow();
              }}
              disabled={Object.values(testRunning).some(Boolean)}
              sx={{
                bgcolor: '#1e40af',
                '&:hover': { bgcolor: '#1d4ed8' },
                fontWeight: 600,
                textTransform: 'none',
                px: 3
              }}
            >
              Run System Tests
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />}
              onClick={testSystemHealth}
              sx={{
                color: '#059669',
                borderColor: '#059669',
                '&:hover': { borderColor: '#047857', bgcolor: 'rgba(5, 150, 105, 0.04)' },
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Quick Health Check
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              startIcon={<LogIcon />}
              onClick={() => setLogDialog(true)}
              sx={{
                color: '#6b7280',
                borderColor: '#6b7280',
                '&:hover': { borderColor: '#4b5563', bgcolor: 'rgba(107, 114, 128, 0.04)' },
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              System Logs ({logs.length})
            </Button>
          </Grid>
          <Grid item>
            <Button 
              variant="outlined" 
              startIcon={<StopIcon />}
              onClick={() => {
                setTestResults({});
                setLogs([]);
                addLog('info', 'general', 'Test results and logs cleared');
              }}
              sx={{
                color: '#ef4444',
                borderColor: '#ef4444',
                '&:hover': { borderColor: '#dc2626', bgcolor: 'rgba(239, 68, 68, 0.04)' },
                fontWeight: 600,
                textTransform: 'none'
              }}
            >
              Clear All
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Test Categories */}
      <Grid container spacing={3}>
        {/* High Priority Tests - Full Width Section */}
        <Grid item xs={12}>
          <Typography variant="h5" sx={{ mb: 2, color: '#1e40af', fontWeight: 'bold' }}>
            🎯 Core Workflow Tests
          </Typography>
          <Grid container spacing={2}>
            {testCategories.filter(cat => cat.priority === 'high').map((category) => {
              const Icon = category.icon;
              const hasResults = testResults[category.id];
              const isRunning = Object.keys(testRunning).some(key => 
                key.startsWith(category.id) && testRunning[key]
              );

              return (
                <Grid item xs={12} sm={6} lg={3} key={category.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: `linear-gradient(135deg, ${category.bgColor} 0%, rgba(255,255,255,0.02) 100%)`,
                      border: `1px solid rgba(30, 64, 175, 0.1)`,
                      '&:hover': {
                        boxShadow: `0 8px 25px rgba(30, 64, 175, 0.15)`,
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Icon sx={{ mr: 1.5, color: category.color, fontSize: '1.8rem' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: category.color }}>
                            {category.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {category.description}
                          </Typography>
                        </Box>
                        {isRunning && <CircularProgress size={20} sx={{ color: category.color }} />}
                      </Box>

                      {/* Test Actions */}
                      <Box sx={{ mb: 2 }}>
                        {category.id === 'users' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              size="small"
                              startIcon={<PersonAddIcon />}
                              onClick={() => {
                                testCreateTestUsers();
                                setTimeout(fetchCurrentUsers, 1000);
                              }}
                              disabled={isRunning}
                              fullWidth
                              sx={{ 
                                bgcolor: category.color,
                                '&:hover': { bgcolor: '#1d4ed8' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              Create Test Users
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={fetchCurrentUsers}
                              disabled={loadingUsers}
                              fullWidth
                              sx={{ 
                                color: category.color,
                                borderColor: category.color,
                                '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.04)' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              {loadingUsers ? 'Loading...' : `Refresh Users (${currentUsers.length})`}
                            </Button>
                          </Box>
                        )}
                        {category.id === 'invitations' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              size="small"
                              onClick={() => {
                                testCreateInvitation('seeker');
                                setTimeout(() => {
                                  fetchInvitations();
                                  refreshChatView();
                                }, 1000);
                              }}
                              disabled={isRunning}
                              fullWidth
                              sx={{ 
                                bgcolor: category.color,
                                '&:hover': { bgcolor: '#1d4ed8' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              Send Test Invitation
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="small"
                              startIcon={<RefreshIcon />}
                              onClick={fetchInvitations}
                              disabled={loadingInvitations}
                              fullWidth
                              sx={{ 
                                color: category.color,
                                borderColor: category.color,
                                '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.04)' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              {loadingInvitations ? 'Loading...' : `Invitations (${invitations.length})`}
                            </Button>
                          </Box>
                        )}
                        {category.id === 'payments' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              size="small"
                              onClick={testPaymentFlow}
                              disabled={isRunning}
                              fullWidth
                              sx={{ 
                                bgcolor: category.color,
                                '&:hover': { bgcolor: '#047857' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              Test Payment Flow
                            </Button>
                          </Box>
                        )}
                        {category.id === 'chat' && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Button 
                              variant="contained" 
                              size="small"
                              onClick={() => {
                                testCreateTestChat();
                                setTimeout(refreshChatView, 1000);
                              }}
                              disabled={isRunning}
                              fullWidth
                              sx={{ 
                                bgcolor: category.color,
                                '&:hover': { bgcolor: '#1d4ed8' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              Initialize Chat
                            </Button>
                            <Button 
                              variant="outlined" 
                              size="small"
                              onClick={() => {
                                setChatViewExpanded(!chatViewExpanded);
                                if (!chatViewExpanded) refreshChatView();
                              }}
                              disabled={isRunning}
                              fullWidth
                              sx={{ 
                                color: category.color,
                                borderColor: category.color,
                                '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.04)' },
                                fontWeight: 600,
                                textTransform: 'none'
                              }}
                            >
                              {chatViewExpanded ? 'Hide Chat' : 'Show Chat'}
                            </Button>
                          </Box>
                        )}
                      </Box>

                      {/* Test Result Status Indicators */}
                      {hasResults && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          {Object.entries(testResults[category.id] || {}).map(([testName, result]) => (
                            <Box key={testName} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                              {getStatusIcon(result.status)}
                              <Typography variant="body2" sx={{ ml: 1, flexGrow: 1, fontSize: '0.85rem' }}>
                                {testName.charAt(0).toUpperCase() + testName.slice(1)}
                              </Typography>
                              <Chip 
                                label={result.status} 
                                color={getStatusColor(result.status)}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: '18px' }}
                              />
                            </Box>
                          ))}
                        </Box>
                      )}

                      {!hasResults && !isRunning && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          Ready to test
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>

        {/* System & Development Tools - Secondary Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mb: 2, mt: 2, color: '#6b7280', fontWeight: 'bold' }}>
            🔧 System & Development Tools
          </Typography>
          <Grid container spacing={2}>
            {testCategories.filter(cat => cat.priority !== 'high').map((category) => {
              const Icon = category.icon;
              const hasResults = testResults[category.id];
              const isRunning = Object.keys(testRunning).some(key => 
                key.startsWith(category.id) && testRunning[key]
              );

              return (
                <Grid item xs={12} sm={6} key={category.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: `linear-gradient(135deg, ${category.bgColor} 0%, rgba(255,255,255,0.02) 100%)`,
                      border: `1px solid ${category.color}20`,
                      '&:hover': {
                        boxShadow: `0 4px 15px ${category.color}25`,
                        transform: 'translateY(-1px)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Icon sx={{ mr: 1.5, color: category.color, fontSize: '1.5rem' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: 'bold', color: category.color }}>
                            {category.label}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {category.description}
                          </Typography>
                        </Box>
                        {isRunning && <CircularProgress size={18} sx={{ color: category.color }} />}
                      </Box>

                      {category.id === 'system' && (
                        <Button 
                          variant="contained" 
                          size="small"
                          onClick={testSystemHealth}
                          disabled={isRunning}
                          fullWidth
                          sx={{ 
                            bgcolor: category.color,
                            '&:hover': { bgcolor: '#4b5563' },
                            fontWeight: 600,
                            textTransform: 'none'
                          }}
                        >
                          Run Health Check
                        </Button>
                      )}

                      {hasResults && (
                        <Box sx={{ mt: 2 }}>
                          {renderTestResults(category.id)}
                        </Box>
                      )}

                      {!hasResults && !isRunning && (
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                          Ready to test
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      </Grid>

      {/* Comprehensive User Management Section */}
      <Grid item xs={12}>
        <Paper 
          sx={{ 
            p: 3, 
            mt: 2,
            background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, rgba(255,255,255,0.05) 100%)',
            border: '1px solid rgba(30, 64, 175, 0.1)'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ color: '#1e40af', fontWeight: 'bold', mb: 1 }}>
                <PersonAddIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                User Management ({currentUsers.length} users)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                View, test, and manage all users in the system
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                startIcon={<RefreshIcon />}
                onClick={fetchCurrentUsers}
                disabled={loadingUsers}
                sx={{
                  color: '#1e40af',
                  borderColor: '#1e40af',
                  '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(30, 64, 175, 0.04)' },
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                {loadingUsers ? 'Loading...' : 'Refresh Users'}
              </Button>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  testCreateTestUsers();
                  setTimeout(fetchCurrentUsers, 1000);
                }}
                disabled={testRunning['users_createUsers']}
                sx={{
                  bgcolor: '#1e40af',
                  '&:hover': { bgcolor: '#1d4ed8' },
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Create Test Users
              </Button>
            </Box>
          </Box>

          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading users...</Typography>
            </Box>
          ) : currentUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                No users found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create test users to start testing user management features
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<PersonAddIcon />}
                onClick={() => {
                  testCreateTestUsers();
                  setTimeout(fetchCurrentUsers, 1000);
                }}
                disabled={testRunning['users_createUsers']}
                sx={{
                  bgcolor: '#1e40af',
                  '&:hover': { bgcolor: '#1d4ed8' },
                  fontWeight: 600,
                  textTransform: 'none'
                }}
              >
                Create Test Users
              </Button>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: '#f8fafc' } }}>
                    <TableCell>User</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentUsers.map((user) => (
                    <TableRow 
                      key={user._id} 
                      sx={{ 
                        '&:hover': { bgcolor: 'rgba(30, 64, 175, 0.04)' },
                        '& td': { py: 2 }
                      }}
                    >
                      {/* User Info */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              mr: 2, 
                              bgcolor: user.userType === 'professional' ? '#059669' : '#1e40af',
                              width: 40,
                              height: 40,
                              fontSize: '1rem',
                              fontWeight: 'bold'
                            }}
                          >
                            {user.firstName?.charAt(0)?.toUpperCase() || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1f2937' }}>
                              {user.firstName} {user.lastName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {user.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: ...{user._id?.slice(-8)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* User Type */}
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Chip 
                            label={user.userType || user.role}
                            size="small"
                            color={user.userType === 'professional' || user.role === 'professional' ? 'success' : 'primary'}
                            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                          />
                          {user.isVerified && (
                            <Chip 
                              label="✓ Verified"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                            />
                          )}
                        </Box>
                      </TableCell>

                      {/* Details */}
                      <TableCell>
                        <Box sx={{ minWidth: 200 }}>
                          {user.occupation && (
                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                              <strong>Role:</strong> {user.occupation}
                            </Typography>
                          )}
                          {user.industry && (
                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                              <strong>Industry:</strong> {user.industry}
                            </Typography>
                          )}
                          {user.experience && (
                            <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.85rem' }}>
                              <strong>Experience:</strong> {user.experience}
                            </Typography>
                          )}
                          {user.location && (
                            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                              <strong>Location:</strong> {user.location}
                            </Typography>
                          )}
                          {!user.occupation && !user.industry && !user.experience && !user.location && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              No additional details
                            </Typography>
                          )}
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Created: {new Date(user.createdAt).toLocaleDateString()}
                          </Typography>
                          <Chip 
                            label={user.email?.includes('test.') || user.email?.includes('@test.') ? 'Test User' : 'Regular User'}
                            size="small"
                            color={user.email?.includes('test.') || user.email?.includes('@test.') ? 'warning' : 'default'}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', mt: 0.5 }}
                          />
                        </Box>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          <Button 
                            variant="outlined"
                            size="small"
                            onClick={() => openProfileDialog(user)}
                            startIcon={<span>👤</span>}
                            sx={{ 
                              minWidth: 'auto', 
                              fontWeight: 600,
                              textTransform: 'none',
                              color: '#6b7280',
                              borderColor: '#6b7280',
                              '&:hover': { borderColor: '#4b5563', bgcolor: 'rgba(107, 114, 128, 0.04)' },
                              fontSize: '0.75rem',
                              px: 1,
                              py: 0.5
                            }}
                          >
                            Profile
                          </Button>
                          <Button 
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              addLog('info', 'users', `Testing login for ${user.firstName} ${user.lastName}`, user);
                              updateTestResult('users', 'testLogin', {
                                status: 'completed',
                                data: user,
                                summary: `Login test for ${user.firstName} ${user.lastName}`
                              });
                            }}
                            startIcon={<span>🔐</span>}
                            sx={{ 
                              minWidth: 'auto', 
                              fontWeight: 600,
                              textTransform: 'none',
                              color: '#059669',
                              borderColor: '#059669',
                              '&:hover': { borderColor: '#047857', bgcolor: 'rgba(5, 150, 105, 0.04)' },
                              fontSize: '0.75rem',
                              px: 1,
                              py: 0.5
                            }}
                          >
                            Test
                          </Button>
                          <Button 
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              navigator.clipboard.writeText(user._id);
                              addLog('success', 'users', `Copied user ID: ${user._id}`, { userId: user._id, name: `${user.firstName} ${user.lastName}` });
                            }}
                            startIcon={<span>📋</span>}
                            sx={{ 
                              minWidth: 'auto', 
                              fontWeight: 600,
                              textTransform: 'none',
                              color: '#f59e0b',
                              borderColor: '#f59e0b',
                              '&:hover': { borderColor: '#d97706', bgcolor: 'rgba(245, 158, 11, 0.04)' },
                              fontSize: '0.75rem',
                              px: 1,
                              py: 0.5
                            }}
                          >
                            Copy
                          </Button>
                          <Button 
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteConfirmDialog(true);
                            }}
                            startIcon={<span>🗑️</span>}
                            disabled={testRunning['users_deleteUser']}
                            sx={{ 
                              minWidth: 'auto', 
                              fontWeight: 600,
                              textTransform: 'none',
                              fontSize: '0.75rem',
                              px: 1,
                              py: 0.5
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* User Management Summary */}
          {currentUsers.length > 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(30, 64, 175, 0.03)', borderRadius: 2, border: '1px solid rgba(30, 64, 175, 0.1)' }}>
              <Typography variant="h6" sx={{ mb: 1, color: '#1e40af', fontWeight: 'bold' }}>
                📊 User Summary
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Total Users</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                    {currentUsers.length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Professionals</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#059669' }}>
                    {currentUsers.filter(u => u.userType === 'professional' || u.role === 'professional').length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Seekers</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                    {currentUsers.filter(u => u.userType === 'seeker' || u.role === 'seeker').length}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2" color="text.secondary">Test Users</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                    {currentUsers.filter(u => u.email?.includes('test.') || u.email?.includes('@test.')).length}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
      </Grid>

      {/* Enhanced Dual-View Chat Simulation */}
      {chatViewExpanded && (
        <Grid item xs={12}>
          <Paper 
            sx={{ 
              p: 3, 
              mt: 2,
              background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, rgba(255,255,255,0.05) 100%)',
              border: '1px solid rgba(30, 64, 175, 0.1)'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ color: '#1e40af', fontWeight: 'bold', mb: 1 }}>
                  <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Dual-View Chat Simulation
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Test real-time messaging between user types without multiple terminals
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => {
                    refreshChatView();
                    fetchInvitations();
                  }}
                  sx={{
                    color: '#1e40af',
                    borderColor: '#1e40af',
                    '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(30, 64, 175, 0.04)' },
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Refresh All
                </Button>
                <Button 
                  variant="contained"
                  size="small"
                  onClick={() => setChatViewExpanded(false)}
                  sx={{
                    bgcolor: '#1e40af',
                    '&:hover': { bgcolor: '#1d4ed8' },
                    fontWeight: 600,
                    textTransform: 'none'
                  }}
                >
                  Collapse Chat
                </Button>
              </Box>
            </Box>
            
            {/* Chat setup and controls */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Button 
                    variant="contained" 
                    startIcon={<ChatIcon />}
                    onClick={testCreateTestChat}
                    disabled={testRunning['chat_createChat']}
                    fullWidth
                    sx={{
                      bgcolor: '#059669',
                      '&:hover': { bgcolor: '#047857' },
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Initialize Chat
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button 
                    variant="outlined" 
                    startIcon={<PersonAddIcon />}
                    onClick={testCreateTestUsers}
                    disabled={testRunning['users_createUsers']}
                    fullWidth
                    sx={{
                      color: '#1e40af',
                      borderColor: '#1e40af',
                      '&:hover': { borderColor: '#1d4ed8', bgcolor: 'rgba(30, 64, 175, 0.04)' },
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Create Test Users
                  </Button>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Button 
                    variant="outlined" 
                    startIcon={<SendIcon />}
                    onClick={() => testCreateInvitation('seeker')}
                    disabled={testRunning['invitations_createInvitation']}
                    fullWidth
                    sx={{
                      color: '#f59e0b',
                      borderColor: '#f59e0b',
                      '&:hover': { borderColor: '#d97706', bgcolor: 'rgba(245, 158, 11, 0.04)' },
                      fontWeight: 600,
                      textTransform: 'none'
                    }}
                  >
                    Send Test Invitation
                  </Button>
                </Grid>
              </Grid>
            </Box>
            
            {/* Dual chat views */}
            <Grid container spacing={3}>
              {/* Seeker View */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  border: '2px solid #1e40af', 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(30, 64, 175, 0.02) 0%, rgba(255,255,255,0.8) 100%)'
                }}>
                  {/* Seeker Header */}
                  <Box sx={{ 
                    bgcolor: '#1e40af', 
                    color: 'white', 
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 1, width: 32, height: 32 }}>
                        S
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Seeker View
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {seekerChatData.currentUser?.name || 'Test Seeker'}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label="SEEKER"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white', 
                        fontWeight: 'bold' 
                      }}
                    />
                  </Box>
                  
                  {/* Current Invitations Banner */}
                  {invitations.filter(inv => 
                    (inv.sender === seekerChatData.currentUser?.id || inv.receiver === seekerChatData.currentUser?.id) &&
                    inv.status === 'pending'
                  ).map((invitation) => (
                    <Box key={invitation._id} sx={{ p: 2, mb: 1, bgcolor: 'rgba(30, 64, 175, 0.1)', borderRadius: 2, border: '1px solid rgba(30, 64, 175, 0.2)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                        ☕ Coffee Chat Invitation
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {invitation.sessionDetails?.topic || 'Coffee Chat'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip label={`${invitation.sessionDetails?.duration || 60}min`} size="small" />
                        <Chip label={invitation.status} size="small" color="warning" />
                      </Box>
                      {seekerChatData.currentUser?.type === 'professional' && invitation.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="contained" 
                            color="success"
                            onClick={() => acceptInvitation(invitation._id)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Accept
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            onClick={() => rejectInvitation(invitation._id)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Decline
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Chat Messages */}
                  <Box sx={{ 
                    height: 350, 
                    overflow: 'auto', 
                    p: 2,
                    bgcolor: 'rgba(250,251,252,0.8)'
                  }}>
                    {seekerChatData.messages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
                        No messages yet. Initialize chat and start testing.
                      </Typography>
                    ) : (
                      seekerChatData.messages.map((message, index) => (
                        <Box key={message.id || index} sx={{ mb: 2 }}>
                          {/* Render invitation card messages as banners */}
                          {message.messageType === 'invitation_card' ? (
                            <Box sx={{ 
                              p: 2, 
                              mb: 2, 
                              bgcolor: 'rgba(30, 64, 175, 0.1)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(30, 64, 175, 0.2)' 
                            }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1e40af' }}>
                                ☕ {message.content}
                              </Typography>
                              {message.metadata?.sessionDetails && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Topic:</strong> {message.metadata.sessionDetails.topic}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Duration:</strong> {message.metadata.sessionDetails.duration} minutes
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Chip 
                                  label={message.metadata?.status || 'pending'} 
                                  size="small" 
                                  color={message.metadata?.status === 'accepted' ? 'success' : 'warning'}
                                />
                                {message.metadata?.payment && (
                                  <Chip 
                                    label={`Payment: ${message.metadata.payment.status}`} 
                                    size="small" 
                                    color="info"
                                  />
                                )}
                              </Box>
                            </Box>
                          ) : (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: message.isOwnMessage ? 'flex-end' : 'flex-start' 
                            }}>
                              <Box sx={{ 
                                maxWidth: '80%',
                                bgcolor: message.isOwnMessage ? '#1e40af' : '#f3f4f6',
                                color: message.isOwnMessage ? 'white' : 'text.primary',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {message.content}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    opacity: 0.7,
                                    fontSize: '0.7rem',
                                    display: 'block'
                                  }}
                                >
                                  {new Date(message.createdAt).toLocaleTimeString()}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                  
                  {/* Message Input */}
                  <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        value={newMessage.seeker}
                        onChange={(e) => setNewMessage(prev => ({ ...prev, seeker: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, 'seeker')}
                        placeholder="Type as seeker..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={testRunning['chat_sendMessage']}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: '#1e40af'
                            }
                          }
                        }}
                      />
                      <Button 
                        variant="contained"
                        onClick={() => sendTestMessage('seeker', newMessage.seeker)}
                        disabled={!newMessage.seeker.trim() || testRunning['chat_sendMessage']}
                        sx={{
                          bgcolor: '#1e40af',
                          '&:hover': { bgcolor: '#1d4ed8' },
                          minWidth: 'auto',
                          px: 2
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              {/* Professional View */}
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  border: '2px solid #059669', 
                  borderRadius: 2, 
                  overflow: 'hidden',
                  background: 'linear-gradient(135deg, rgba(5, 150, 105, 0.02) 0%, rgba(255,255,255,0.8) 100%)'
                }}>
                  {/* Professional Header */}
                  <Box sx={{ 
                    bgcolor: '#059669', 
                    color: 'white', 
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 1, width: 32, height: 32 }}>
                        P
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Professional View
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          {professionalChatData.currentUser?.name || 'Test Professional'}
                        </Typography>
                      </Box>
                    </Box>
                    <Chip 
                      label="PROFESSIONAL"
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(255,255,255,0.2)', 
                        color: 'white', 
                        fontWeight: 'bold' 
                      }}
                    />
                  </Box>
                  
                  {/* Current Invitations Banner */}
                  {invitations.filter(inv => 
                    (inv.sender === professionalChatData.currentUser?.id || inv.receiver === professionalChatData.currentUser?.id) &&
                    inv.status === 'pending'
                  ).map((invitation) => (
                    <Box key={invitation._id} sx={{ p: 2, mb: 1, bgcolor: 'rgba(5, 150, 105, 0.1)', borderRadius: 2, border: '1px solid rgba(5, 150, 105, 0.2)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        ☕ Coffee Chat Invitation
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {invitation.sessionDetails?.topic || 'Coffee Chat'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <Chip label={`${invitation.sessionDetails?.duration || 60}min`} size="small" />
                        <Chip label={invitation.status} size="small" color="warning" />
                      </Box>
                      {professionalChatData.currentUser?.type === 'professional' && invitation.status === 'pending' && (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button 
                            size="small" 
                            variant="contained" 
                            onClick={() => acceptInvitation(invitation._id)}
                            sx={{ 
                              bgcolor: '#059669',
                              '&:hover': { bgcolor: '#047857' },
                              minWidth: 'auto', 
                              px: 2 
                            }}
                          >
                            Accept
                          </Button>
                          <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            onClick={() => rejectInvitation(invitation._id)}
                            sx={{ minWidth: 'auto', px: 2 }}
                          >
                            Decline
                          </Button>
                        </Box>
                      )}
                    </Box>
                  ))}
                  
                  {/* Chat Messages */}
                  <Box sx={{ 
                    height: 350, 
                    overflow: 'auto', 
                    p: 2,
                    bgcolor: 'rgba(250,251,252,0.8)'
                  }}>
                    {professionalChatData.messages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 4 }}>
                        No messages yet. Initialize chat and start testing.
                      </Typography>
                    ) : (
                      professionalChatData.messages.map((message, index) => (
                        <Box key={message.id || index} sx={{ mb: 2 }}>
                          {/* Render invitation card messages as banners */}
                          {message.messageType === 'invitation_card' ? (
                            <Box sx={{ 
                              p: 2, 
                              mb: 2, 
                              bgcolor: 'rgba(5, 150, 105, 0.1)', 
                              borderRadius: 2, 
                              border: '1px solid rgba(5, 150, 105, 0.2)' 
                            }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#059669' }}>
                                ☕ {message.content}
                              </Typography>
                              {message.metadata?.sessionDetails && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Topic:</strong> {message.metadata.sessionDetails.topic}
                                  </Typography>
                                  <Typography variant="body2">
                                    <strong>Duration:</strong> {message.metadata.sessionDetails.duration} minutes
                                  </Typography>
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Chip 
                                  label={message.metadata?.status || 'pending'} 
                                  size="small" 
                                  color={message.metadata?.status === 'accepted' ? 'success' : 'warning'}
                                />
                                {message.metadata?.payment && (
                                  <Chip 
                                    label={`Payment: ${message.metadata.payment.status}`} 
                                    size="small" 
                                    color="info"
                                  />
                                )}
                              </Box>
                              {professionalChatData.currentUser?.type === 'professional' && message.metadata?.status === 'pending' && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                  <Button 
                                    size="small" 
                                    variant="contained" 
                                    onClick={() => acceptInvitation(message.metadata?.invitationId)}
                                    sx={{ 
                                      bgcolor: '#059669',
                                      '&:hover': { bgcolor: '#047857' },
                                      minWidth: 'auto', 
                                      px: 2 
                                    }}
                                  >
                                    Accept
                                  </Button>
                                  <Button 
                                    size="small" 
                                    variant="outlined" 
                                    color="error"
                                    onClick={() => rejectInvitation(message.metadata?.invitationId)}
                                    sx={{ minWidth: 'auto', px: 2 }}
                                  >
                                    Decline
                                  </Button>
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: message.isOwnMessage ? 'flex-end' : 'flex-start' 
                            }}>
                              <Box sx={{ 
                                maxWidth: '80%',
                                bgcolor: message.isOwnMessage ? '#059669' : '#f3f4f6',
                                color: message.isOwnMessage ? 'white' : 'text.primary',
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                              }}>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  {message.content}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    opacity: 0.7,
                                    fontSize: '0.7rem',
                                    display: 'block'
                                  }}
                                >
                                  {new Date(message.createdAt).toLocaleTimeString()}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))
                    )}
                  </Box>
                  
                  {/* Message Input */}
                  <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid #e5e7eb' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        value={newMessage.professional}
                        onChange={(e) => setNewMessage(prev => ({ ...prev, professional: e.target.value }))}
                        onKeyPress={(e) => handleKeyPress(e, 'professional')}
                        placeholder="Type as professional..."
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={testRunning['chat_sendMessage']}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: '#059669'
                            }
                          }
                        }}
                      />
                      <Button 
                        variant="contained"
                        onClick={() => sendTestMessage('professional', newMessage.professional)}
                        disabled={!newMessage.professional.trim() || testRunning['chat_sendMessage']}
                        sx={{
                          bgcolor: '#059669',
                          '&:hover': { bgcolor: '#047857' },
                          minWidth: 'auto',
                          px: 2
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>
            
            {/* Connection Status */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Chip 
                icon={seekerChatData.thread ? <CheckIcon /> : <ErrorIcon />}
                label={`Seeker: ${seekerChatData.thread ? 'Connected' : 'Not Connected'}`}
                color={seekerChatData.thread ? 'success' : 'error'}
                variant="outlined"
                size="small"
              />
              <Chip 
                icon={professionalChatData.thread ? <CheckIcon /> : <ErrorIcon />}
                label={`Professional: ${professionalChatData.thread ? 'Connected' : 'Not Connected'}`}
                color={professionalChatData.thread ? 'success' : 'error'}
                variant="outlined"
                size="small"
              />
              <Chip 
                icon={seekerChatData.messages.length > 0 || professionalChatData.messages.length > 0 ? <CheckIcon /> : <InfoIcon />}
                label={`Messages: ${Math.max(seekerChatData.messages.length, professionalChatData.messages.length)}`}
                color={seekerChatData.messages.length > 0 || professionalChatData.messages.length > 0 ? 'success' : 'default'}
                variant="outlined"
                size="small"
              />
            </Box>
          </Paper>
        </Grid>
      )}

      {/* User Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
        <DialogTitle>⚠️ Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{userToDelete?.firstName} {userToDelete?.lastName}</strong>?
          </Typography>
          <Typography color="error" sx={{ mt: 1 }}>
            This action cannot be undone and will remove all associated data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={() => {
              if (userToDelete) {
                deleteUser(userToDelete._id);
                setDeleteConfirmDialog(false);
                setUserToDelete(null);
              }
            }}
            color="error" 
            variant="contained"
            disabled={testRunning['users_deleteUser']}
          >
            {testRunning['users_deleteUser'] ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Profile Edit Dialog */}
      <Dialog open={profileDialog} onClose={() => setProfileDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          👤 Profile Testing - {selectedUserProfile?.firstName} {selectedUserProfile?.lastName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Edit user information below. Changes will be saved to the database and visible immediately in the table.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="First Name"
                value={profileEditData.firstName || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, firstName: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Last Name"
                value={profileEditData.lastName || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, lastName: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                value={profileEditData.email || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, email: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                type="email"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Occupation"
                value={profileEditData.occupation || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, occupation: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., Software Engineer"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Industry"
                value={profileEditData.industry || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, industry: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., Technology"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Experience"
                value={profileEditData.experience || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, experience: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., 5+ years"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Location"
                value={profileEditData.location || ''}
                onChange={(e) => setProfileEditData(prev => ({ ...prev, location: e.target.value }))}
                fullWidth
                variant="outlined"
                size="small"
                placeholder="e.g., San Francisco, CA"
              />
            </Grid>
          </Grid>
          
          {/* Show changes preview */}
          {selectedUserProfile && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(30, 64, 175, 0.05)', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#1e40af', fontWeight: 'bold' }}>
                📋 Changes Preview
              </Typography>
              {Object.keys(profileEditData).map(key => {
                const oldValue = selectedUserProfile[key] || '';
                const newValue = profileEditData[key] || '';
                const hasChanged = oldValue !== newValue;
                
                if (!hasChanged) return null;
                
                return (
                  <Typography key={key} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> 
                    <span style={{ textDecoration: 'line-through', color: '#ef4444', marginLeft: '8px' }}>
                      {oldValue || 'empty'}
                    </span>
                    <span style={{ color: '#059669', marginLeft: '8px' }}>
                      → {newValue || 'empty'}
                    </span>
                  </Typography>
                );
              })}
              {Object.keys(profileEditData).every(key => 
                (selectedUserProfile[key] || '') === (profileEditData[key] || '')
              ) && (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  No changes made
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProfileDialog(false)}>Cancel</Button>
          <Button 
            onClick={saveProfileChanges}
            variant="contained"
            disabled={testRunning['users_profileUpdate']}
            sx={{
              bgcolor: '#1e40af',
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            {testRunning['users_profileUpdate'] ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Log Dialog */}
      <Dialog open={logDialog} onClose={() => setLogDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          System Logs
          <Button 
            onClick={() => setLogs([])}
            size="small"
            sx={{ float: 'right' }}
          >
            Clear Logs
          </Button>
        </DialogTitle>
        <DialogContent>
          <List>
            {logs.map((log) => (
              <ListItem key={log.id} divider>
                <ListItemIcon>
                  {getStatusIcon(log.level)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography variant="body2" component="span">
                        [{log.category}] {log.message}
                      </Typography>
                      <Chip 
                        label={log.level}
                        size="small"
                        color={getStatusColor(log.level)}
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption">
                        {new Date(log.timestamp).toLocaleString()} | {log.user}
                      </Typography>
                      {log.data && (
                        <pre style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
                          {JSON.stringify(log.data, null, 2)}
                        </pre>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
            {logs.length === 0 && (
              <ListItem>
                <ListItemText primary="No logs available" />
              </ListItem>
            )}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TestDashboard;